import { type WeeklySchedule } from "@/services/firebase/functions"

/**
 * Formats a WeeklySchedule into a human-readable string.
 * Groups consecutive days with the same hours.
 * Example output: "Mo-Fr 8:00-18:00, Sa 8:00-14:00 Uhr"
 */
export const formatOpeningHours = (schedule: WeeklySchedule | null | undefined): string => {
  if (!schedule) {
    return ""
  }

  // Extract days with their hours
  const days = [
    { name: "Mo", key: "monday", schedule: schedule.monday },
    { name: "Di", key: "tuesday", schedule: schedule.tuesday },
    { name: "Mi", key: "wednesday", schedule: schedule.wednesday },
    { name: "Do", key: "thursday", schedule: schedule.thursday },
    { name: "Fr", key: "friday", schedule: schedule.friday },
    { name: "Sa", key: "saturday", schedule: schedule.saturday },
    { name: "So", key: "sunday", schedule: schedule.sunday },
  ]

  // Group consecutive days with same hours
  const groups: Array<{ days: string[]; hours: string }> = []
  let currentGroup: { days: string[]; hours: string } | null = null

  for (const day of days) {
    const daySchedule = day.schedule

    if (daySchedule?.isOpen && daySchedule.open && daySchedule.close) {
      const hours = `${daySchedule.open}-${daySchedule.close}`

      if (currentGroup && currentGroup.hours === hours) {
        // Add to current group
        currentGroup.days.push(day.name)
      } else {
        // Start new group
        if (currentGroup) {
          groups.push(currentGroup)
        }
        currentGroup = { days: [day.name], hours }
      }
    } else {
      // Day is closed, finish current group
      if (currentGroup) {
        groups.push(currentGroup)
        currentGroup = null
      }
    }
  }

  // Add last group
  if (currentGroup) {
    groups.push(currentGroup)
  }

  // Format groups into readable string
  if (groups.length === 0) {
    return "Geschlossen"
  }

  const formatted = groups
    .map((group) => {
      if (group.days.length === 1) {
        return `${group.days[0]} ${group.hours}`
      } else if (group.days.length === 2) {
        return `${group.days[0]}-${group.days[1]} ${group.hours}`
      } else {
        return `${group.days[0]}-${group.days[group.days.length - 1]} ${group.hours}`
      }
    })
    .join(", ")

  return `${formatted} Uhr`
}

/**
 * Returns opening hours as an array of lines (one per group).
 * Example: ["Mo–Fr 8:00–18:00 Uhr", "Sa 8:00–14:00 Uhr"]
 */
export const getOpeningHoursLines = (schedule: WeeklySchedule | null | undefined): string[] => {
  if (!schedule) return []

  const days = [
    { name: "Mo", schedule: schedule.monday },
    { name: "Di", schedule: schedule.tuesday },
    { name: "Mi", schedule: schedule.wednesday },
    { name: "Do", schedule: schedule.thursday },
    { name: "Fr", schedule: schedule.friday },
    { name: "Sa", schedule: schedule.saturday },
    { name: "So", schedule: schedule.sunday },
  ]

  const groups: Array<{ days: string[]; hours: string }> = []
  let currentGroup: { days: string[]; hours: string } | null = null

  for (const day of days) {
    const d = day.schedule
    if (d?.isOpen && d.open && d.close) {
      const hours = `${d.open}\u2013${d.close}`
      if (currentGroup && currentGroup.hours === hours) {
        currentGroup.days.push(day.name)
      } else {
        if (currentGroup) groups.push(currentGroup)
        currentGroup = { days: [day.name], hours }
      }
    } else {
      if (currentGroup) {
        groups.push(currentGroup)
        currentGroup = null
      }
    }
  }
  if (currentGroup) groups.push(currentGroup)

  return groups.map((g) => {
    const label = g.days.length === 1 ? g.days[0] : `${g.days[0]}\u2013${g.days[g.days.length - 1]}`
    return `${label} ${g.hours} Uhr`
  })
}
